import axios from "axios";
import { isGuestRole } from "../utils/emailVerification";
import { getJwtExpiryMs } from "../utils/jwtUtils";
import { loginAsGuest, logout } from "../utils/authUtils";

// Logout slightly before exp so in-flight requests do not fail at T=exp.
export const TOKEN_EXPIRY_SKEW_MS = 5000;
export const MAX_TIMEOUT_MS = 2147483647;

// Legacy delivery-settings endpoints can answer 401 to a valid session token;
// that must not log the admin out when opening the shipping settings page.
const AUTH_401_SKIP_EXACT_PATHS = [
  "/auth/login",
  "/api/StationDeliveryHours",
  "/api/date_type",
  "/api/date_day_of_week",
  "/api/date_sepcial",
];
const AUTH_401_SKIP_PREFIX_PATHS = ["/auth/password-reset"];

const readPathname = (url = "") => {
  try {
    return new URL(String(url), "https://local.invalid").pathname;
  } catch {
    return String(url);
  }
};

const pathnameMatches = (pathname, path, { allowSubpaths = false } = {}) => {
  if (pathname === path || pathname.endsWith(path)) {
    return true;
  }
  return allowSubpaths && pathname.includes(`${path}/`);
};

export const isSkippedAuthUrl = (url = "") => {
  const pathname = readPathname(url);
  return (
    AUTH_401_SKIP_EXACT_PATHS.some((path) => pathnameMatches(pathname, path)) ||
    AUTH_401_SKIP_PREFIX_PATHS.some((path) =>
      pathnameMatches(pathname, path, { allowSubpaths: true }),
    )
  );
};

let sessionExpiryInProgress = false;

export const resetSessionExpiryGuard = () => {
  sessionExpiryInProgress = false;
};

const readAuthorizationHeader = (headers = {}) =>
  headers.Authorization || headers.authorization;

const readBearerToken = (authHeader) =>
  String(authHeader)
    .replace(/^Bearer\s+/i, "")
    .trim();

export const isUnauthorizedSessionError = (error) => {
  if (error?.response?.status !== 401) {
    return false;
  }
  if (isSkippedAuthUrl(error.config?.url)) {
    return false;
  }

  const authHeader = readAuthorizationHeader(error.config?.headers);
  if (!authHeader) {
    return false;
  }

  const currentJwt = localStorage.getItem("jwtToken");
  if (!currentJwt) {
    return false;
  }

  return readBearerToken(authHeader) === currentJwt;
};

export const getTokenExpiryDelayMs = (token, now = Date.now()) => {
  const expiryMs = getJwtExpiryMs(token);
  if (expiryMs == null) {
    return null;
  }
  return expiryMs - TOKEN_EXPIRY_SKEW_MS - now;
};

export const shouldExpireTokenNow = (token, now = Date.now()) => {
  const delay = getTokenExpiryDelayMs(token, now);
  return delay != null && delay <= 0;
};

export const handleExpiredSession = ({ dispatch, navigate }) => {
  if (sessionExpiryInProgress) {
    return false;
  }

  sessionExpiryInProgress = true;

  if (isGuestRole()) {
    localStorage.removeItem("jwtToken");
    loginAsGuest(dispatch);
    return true;
  }

  logout({ dispatch, navigate });
  return true;
};

export const installSessionExpiryInterceptor = (onExpired) => {
  const interceptorId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isUnauthorizedSessionError(error)) {
        onExpired();
      }
      return Promise.reject(error);
    },
  );

  return () => {
    axios.interceptors.response.eject(interceptorId);
  };
};
